export type ActivityUnit = 'reps' | 'seconds';

interface ActivityDefinition {
  unit: ActivityUnit;
  pointsPerStep: number;
  min: number;
  step: number;
  defaultQty: number;
  singular: string;
  plural: string;
}

function reps(pointsPerStep: number, singular: string, plural: string): ActivityDefinition {
  return { unit: 'reps', pointsPerStep, min: 1, step: 1, defaultQty: 10, singular, plural };
}

export const activityDefinitions = {
  Pushups: reps(2, 'pushup', 'pushups'),
  'Pull Ups': reps(4, 'pull-up', 'pull-ups'),
  Squats: reps(1, 'squat', 'squats'),
  Burpees: reps(3, 'burpee', 'burpees'),
  'Sit-ups': reps(1, 'sit-up', 'sit-ups'),
  'Jumping Jacks': reps(1, 'jumping jack', 'jumping jacks'),
  Plank: {
    unit: 'seconds', pointsPerStep: 1, min: 5, step: 5, defaultQty: 30,
    singular: 'second of plank', plural: 'seconds of plank',
  } satisfies ActivityDefinition,
  Hang: {
    unit: 'seconds', pointsPerStep: 1, min: 5, step: 5, defaultQty: 30,
    singular: 'second hanging from a bar', plural: 'seconds hanging from a bar',
  } satisfies ActivityDefinition,
};

export type ActivityType = keyof typeof activityDefinitions;
export const activityTypes = Object.keys(activityDefinitions) as ActivityType[];

export function isActivityType(value: unknown): value is ActivityType {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(activityDefinitions, value);
}

/** Normalize before both scoring and persistence; rep quantities retain their existing behavior. */
export function normalizeActivityQuantity(type: ActivityType, value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
  const definition = activityDefinitions[type];
  if (definition.unit === 'reps') return value;
  const qty = Math.floor(value / definition.step) * definition.step;
  return qty >= definition.min ? qty : null;
}

export function getActivityBasePoints(type: ActivityType, qty: number): number {
  const normalized = normalizeActivityQuantity(type, qty);
  if (normalized === null) throw new Error('Invalid activity quantity');
  // Divide by the duration step to keep timed activity scores exact whole numbers.
  const definition = activityDefinitions[type];
  return normalized / definition.step * definition.pointsPerStep;
}

function formatQuantity(qty: number): string {
  return Number.isInteger(qty) ? String(qty) : qty.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

export function formatActivity(type: ActivityType, qty: number): string {
  return activityDefinitions[type].unit === 'seconds'
    ? `${formatQuantity(qty)}s ${type}`
    : `${formatQuantity(qty)} ${type}`;
}

export function formatActivityForNotification(type: string, qty: number): string {
  if (!isActivityType(type)) return `${formatQuantity(qty)} ${type}`;
  const definition = activityDefinitions[type];
  return `${formatQuantity(qty)} ${qty === 1 ? definition.singular : definition.plural}`;
}
